/**
 * TestResultInsightCardWithDetail
 *
 * App-level wrapper that composes the presentation-only
 * `TestResultInsightCard` (in `src/packages/homepage/plan/components/`)
 * with the canonical `TestResultDetailDialog`.
 *
 * The card lives in packages/ and can't import from apps/ (cross-app
 * boundary lint rule). This wrapper handles the tap → sheet-open
 * behavior on Home surfaces so callers just drop it in place of
 * `TestResultInsightCard`.
 *
 * Responsibilities:
 * - Own the open/close state for the result dialog.
 * - Convert `ResultInterpretation` + raw test-result history +
 *   biomarker factsheet → the `LabResult` shape the dialog expects.
 * - Pass through `useUpdateTestResult` / `useDeleteTestResult` so
 *   the dialog's Edit / Delete actions work (per issue #1593 product
 *   decision: edit/delete belongs on the fact-sheet).
 *
 * See issue #1593 for the IA rationale.
 */

import { useMemo, useState } from "react";
import { View } from "react-native";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { useLocale } from "@src/packages/i18n/useLocale";
import {
  useTestResults,
  useDeleteTestResult,
  useUpdateTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import { groupCanonicalTestResults } from "@src/packages/test-results/canonical-results";
import { getBiomarkerFactsheet } from "@src/packages/test-results/content/biomarker-factsheets";
import { getBiomarkerIdentity } from "@src/packages/test-results/biomarker-identity";
import TestResultInsightCard, {
  type TestResultInsightCardState,
} from "@src/packages/homepage/plan/components/TestResultInsightCard";
import type { ResultInterpretation } from "@src/packages/homepage/plan/intelligence/useTestResultInsights";
import type { Locale } from "@src/packages/i18n/locale";
import TestResultDetailDialog from "./TestResultDetailDialog";
import type { LabResult } from "../screens/care/sections/ResultsLabsCard";
import {
  getCanonicalTestIdForDataPoint,
  getCanonicalTestIdForMarkerName,
  getTestRouteTarget,
} from "../screens/access/testRouteIds";

type Props = {
  interpretation: ResultInterpretation;
  state?: TestResultInsightCardState;
  partnerName?: string;
};

/**
 * Build a `LabResult` from a `ResultInterpretation` + the raw test
 * history the sheet needs. Uses `getBiomarkerFactsheet` so "What is
 * it" / "Why it matters" / "Typical range" come from the same source
 * as `AllLabResultsScreen` (see the pattern at `AllLabResultsScreen
 * .tsx:245`).
 */
function buildLabResult(
  interpretation: ResultInterpretation,
  allResults: ReturnType<typeof useTestResults>["data"],
  formatDate: (d: string) => string,
  locale: Locale,
): LabResult {
  const testName = interpretation.testName;
  const factsheet = getBiomarkerFactsheet(testName, locale);
  const sourceResult = (allResults ?? []).find(
    (result) => result.id === interpretation.sourceResultId,
  );
  const biomarkerIdentity = getBiomarkerIdentity(
    sourceResult?.name ?? testName,
  );

  // Use the shared canonical history so insight cards, All Results, and
  // consolidated test factsheets agree on aliases, latest-first ordering,
  // trend, and source/provenance metadata.
  const canonicalHistory = groupCanonicalTestResults(allResults ?? []).find(
    (historyGroup) =>
      historyGroup.identity === biomarkerIdentity ||
      historyGroup.results.some((result) => result.id === sourceResult?.id),
  );
  const latest = canonicalHistory?.latest;
  const history =
    canonicalHistory?.results.map((result) => ({
      id: result.id,
      date: result.date ? formatDate(result.date) : "",
      value: result.value,
      clinicalAssessment: result.clinicalAssessment,
      clinicalAssessmentDetails: result.clinicalAssessmentDetails,
      attachment: result.source.attachment,
      provenance: result.source,
    })) ?? [];

  return {
    id: interpretation.sourceResultId ?? testName,
    testId:
      getCanonicalTestIdForDataPoint(canonicalHistory?.dataPointId) ??
      getCanonicalTestIdForMarkerName(testName),
    name: testName,
    value: interpretation.value,
    unit: interpretation.unit,
    date: interpretation.sourceResultDate
      ? formatDate(interpretation.sourceResultDate)
      : "",
    cycleId: latest?.cycleId ?? null,
    ...(factsheet ?? {}),
    trend: canonicalHistory?.trend ?? "none",
    isAbnormal:
      interpretation.category === "low" || interpretation.category === "high",
    clinicalAssessment:
      interpretation.category === "borderline"
        ? "unknown"
        : interpretation.category,
    history: history.length > 1 ? history : undefined,
    interpretationContext: interpretation.interpretationContext,
    attachment: latest?.source.attachment,
    provenance: latest?.source,
  };
}

export default function TestResultInsightCardWithDetail({
  interpretation,
  state,
  partnerName,
}: Props) {
  const [detailOpen, setDetailOpen] = useState(false);
  const navigation = useNavigation();
  const { data: testResults } = useTestResults();
  const { abbrev } = useDateFormat();
  const { locale } = useLocale();
  const updateTestResult = useUpdateTestResult();
  const deleteTestResult = useDeleteTestResult();
  const labResult = useMemo(
    () => buildLabResult(interpretation, testResults, abbrev, locale),
    [interpretation, testResults, abbrev, locale],
  );

  return (
    <View>
      <TestResultInsightCard
        interpretation={interpretation}
        state={state}
        partnerName={partnerName}
        onPress={() => setDetailOpen(true)}
      />
      <TestResultDetailDialog
        result={detailOpen ? labResult : null}
        point={
          detailOpen
            ? {
                id: labResult.id,
                date: labResult.date,
                value: labResult.value,
                clinicalAssessment: labResult.clinicalAssessment,
                clinicalAssessmentDetails: labResult.clinicalAssessmentDetails,
                attachment: labResult.attachment,
                provenance: labResult.provenance,
              }
            : null
        }
        onClose={() => setDetailOpen(false)}
        onAttachSource={async (id, attachmentId) => {
          await updateTestResult.mutateAsync({
            testResultId: id,
            data: { attachmentId },
          });
        }}
        onEdit={async (id, data) => {
          await updateTestResult.mutateAsync({ testResultId: id, data });
          setDetailOpen(false);
        }}
        onDelete={async (id) => {
          await deleteTestResult.mutateAsync(id);
          setDetailOpen(false);
        }}
        testId={labResult.testId}
        onOpenTest={(testId) => {
          setDetailOpen(false);
          const target = getTestRouteTarget(testId);
          navigation.dispatch(
            CommonActions.navigate({
              name: "Access",
              params: {
                screen: target.screen,
                params: { ...target.params, returnTab: "Home" },
              },
            }),
          );
        }}
      />
    </View>
  );
}
